import adminApi from './adminApi';

export interface GuestAnalyticsSummaryDTO {
  uniqueGuests: number;
  newSessions: number;
  coursesRegistered: number;
  topicsAccessed: number;
  signupCtasClicked: number;
  from: string;
  to: string;
}

export interface GuestAnalyticsTimeseriesPointDTO {
  bucket: string;
  uniqueGuests: number;
}

export interface GuestAnalyticsEntryMethodDTO {
  entryMethod: string;
  guestCount: number;
  sharePercent: number;
}

export interface GuestAnalyticsGeographyRowDTO {
  locationId: number | null;
  locationName: string;
  guestCount: number;
  registrationPercent: number;
}

export interface GuestAnalyticsFunnelStageDTO {
  stageId: string;
  label: string;
  guestCount: number;
  conversionToNextPercent: number | null;
}

export type GuestAnalyticsGranularity = 'daily' | 'weekly' | 'monthly';

export interface GuestAnalyticsDateParams {
  from?: string;
  to?: string;
}

/** Default: last 7 days including today (UTC), matching backend. */
export function defaultGuestAnalyticsRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 6);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

function unwrap<T>(response: { data: { data?: T } & T }): T {
  return (response.data?.data ?? response.data) as T;
}

export async function getGuestAnalyticsSummary(
  params: GuestAnalyticsDateParams = {}
): Promise<GuestAnalyticsSummaryDTO> {
  const response = await adminApi.get('/api/admin/guest-analytics/summary', { params });
  return unwrap(response);
}

export async function getGuestAnalyticsTimeseries(
  params: GuestAnalyticsDateParams & { granularity?: GuestAnalyticsGranularity } = {}
): Promise<GuestAnalyticsTimeseriesPointDTO[]> {
  const response = await adminApi.get('/api/admin/guest-analytics/timeseries', { params });
  return unwrap(response);
}

export async function getGuestAnalyticsEntryMethods(
  params: GuestAnalyticsDateParams = {}
): Promise<GuestAnalyticsEntryMethodDTO[]> {
  const response = await adminApi.get('/api/admin/guest-analytics/entry-methods', { params });
  return unwrap(response);
}

export async function getGuestAnalyticsGeography(
  params: GuestAnalyticsDateParams & {
    level?: 'country' | 'council' | 'school';
    countryId?: number;
    councilId?: number;
  } = {}
): Promise<GuestAnalyticsGeographyRowDTO[]> {
  const response = await adminApi.get('/api/admin/guest-analytics/geography', { params });
  return unwrap(response);
}

export async function getGuestAnalyticsFunnel(
  params: GuestAnalyticsDateParams & { entryMethod?: string } = {}
): Promise<GuestAnalyticsFunnelStageDTO[]> {
  const response = await adminApi.get('/api/admin/guest-analytics/funnel', { params });
  return unwrap(response);
}
